// ============================================
// HOOK - Camera Barcode Scanner
// ============================================

import { useEffect, useRef, useState, useCallback } from 'react';

interface UseCameraBarcodeScaneOptions {
  enabled: boolean;
  onScan?: (barcode: string) => void;
  scanInterval?: number;
}

export const useCameraBarcodeScan = ({
  enabled,
  onScan,
  scanInterval = 300,
}: UseCameraBarcodeScaneOptions) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detectedCode, setDetectedCode] = useState<string | null>(null);
  
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const scanIntervalRef = useRef<number | null>(null);
  const barcodeDetectorRef = useRef<any>(null);
  const currentVideoRef = useRef<HTMLVideoElement | null>(null);

  const initBarcodeDetector = useCallback(async () => {
    try {
      if ('BarcodeDetector' in window) {
        const formats = await (window as any).BarcodeDetector.getSupportedFormats();
        console.log('Formatos soportados:', formats);
        barcodeDetectorRef.current = new (window as any).BarcodeDetector({
          formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'code_93', 'codabar', 'itf', 'qr_code']
        });
        return true;
      } else {
        console.warn('BarcodeDetector no disponible, usando ZXing');
        return false;
      }
    } catch (err) {
      console.error('Error inicializando BarcodeDetector:', err);
      return false;
    }
  }, []);

  const scanWithZXing = async (canvas: HTMLCanvasElement) => {
    try {
      const { BrowserMultiFormatReader } = await import('@zxing/library');
      const codeReader = new BrowserMultiFormatReader();
      
      // Convertir canvas a data URL para ZXing
      const dataUrl = canvas.toDataURL('image/png');
      const result = await codeReader.decodeFromImageUrl(dataUrl);
      
      if (result) {
        handleDetectedCode(result.getText());
      }
    } catch (err) {
      // No hacer nada si no se detecta código
    }
  };

  const handleDetectedCode = useCallback((code: string) => {
    if (code !== detectedCode) {
      setDetectedCode(code);
      onScan?.(code);
      setTimeout(() => setDetectedCode(null), 2000);
    }
  }, [detectedCode, onScan]);

  const scanFrame = useCallback(async (video: HTMLVideoElement, useNativeDetector: boolean) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    try {
      if (useNativeDetector && barcodeDetectorRef.current) {
        const barcodes = await barcodeDetectorRef.current.detect(canvas);
        if (barcodes.length > 0) {
          handleDetectedCode(barcodes[0].rawValue);
        }
      } else {
        await scanWithZXing(canvas);
      }
    } catch (err) {}
  }, [handleDetectedCode]);

  const startScanning = useCallback(async (videoElement?: HTMLVideoElement | null) => {
    const video = videoElement || currentVideoRef.current;
    
    if (!video) {
      setError('Elemento de video no disponible');
      console.error('videoElement is null');
      return;
    }

    currentVideoRef.current = video;

    try {
      console.log('Solicitando acceso a cámara...');
      setError(null);
      
      const constraints: MediaStreamConstraints = {
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('Stream obtenido');
      streamRef.current = stream;
      
      video.srcObject = stream;
      await video.play();
      console.log('Video reproduciendo');
      
      setIsScanning(true);
      
      const hasNativeDetector = await initBarcodeDetector();

      if (!canvasRef.current) {
        canvasRef.current = document.createElement('canvas');
      }

      scanIntervalRef.current = window.setInterval(() => {
        if (video.readyState === video.HAVE_ENOUGH_DATA) {
          scanFrame(video, hasNativeDetector);
        }
      }, scanInterval);

    } catch (err) {
      console.error('Error al acceder a la cámara:', err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setError('Permiso de cámara denegado.');
        } else if (err.name === 'NotFoundError') {
          setError('No se encontró cámara.');
        } else {
          setError('Error: ' + err.message);
        }
      } else {
        setError('Error desconocido');
      }
      setIsScanning(false);
    }
  }, [scanInterval, initBarcodeDetector, scanFrame]);

  const stopScanning = useCallback(() => {
    console.log('Deteniendo escaneo...');
    
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (currentVideoRef.current) {
      currentVideoRef.current.srcObject = null;
    }

    setIsScanning(false);
    setDetectedCode(null);
  }, []);

  useEffect(() => {
    if (!enabled && isScanning) {
      stopScanning();
    }
    return () => {
      if (isScanning) {
        stopScanning();
      }
    };
  }, [enabled, isScanning, stopScanning]);

  return { startScanning, stopScanning, isScanning, error, detectedCode };
};
